import { useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { Alert } from '@ds/feedback';
import { Select, Switch } from '@ds/forms';
import { PageHeader } from '@/comuns/PageHeader';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { flagLigadaPara } from '@/data/plataformaDaVecchia';
import { dados } from '@/data/porta';
import { pode } from '@/session/sessao';
import type { CargaDasFlags, FlagDeModulo } from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { KillSwitchModal, RolloutModal } from './RolloutDaFlag';

export async function carregarFlags(): Promise<CargaDasFlags> {
    return dados().flags();
}

export function Flags() {
    const carga = useLoaderData() as CargaDasFlags;
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const [clinicaId, setClinicaId] = useState(carga.clinicas[0]?.id ?? '');
    const [emCurso, setEmCurso] = useState<string | null>(null);
    const [rollout, setRollout] = useState<FlagDeModulo | null>(null);
    const [kill, setKill] = useState<FlagDeModulo | null>(null);

    const clinica = carga.clinicas.find((c) => c.id === clinicaId);
    const alterna = pode('flags.alternar');

    const [confirmar, confirmacao] = useConfirmacao();

    const mudarPadrao = async (flag: FlagDeModulo, ativo: boolean) => {
        if (!ativo) {
            const ok = await confirmar({
                titulo: `Desligar ${flag.descricao} para a base?`,
                descricao: 'O módulo some do menu e da rota, na hora, de toda clínica que não tem exceção ligada.',
                confirmar: 'Desligar para a base',
            });
            if (!ok) return;
        }
        setEmCurso(flag.id);
        try {
            await dados().definirPadraoDaFlag(flag.id, ativo);
            aviso({
                title: ativo ? 'Módulo ligado para a base' : 'Módulo desligado para a base',
                description: `${flag.descricao} · as exceções por clínica continuam valendo`,
            });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível mudar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setEmCurso(null);
        }
    };

    const mudarExcecao = async (flag: FlagDeModulo, ativo: boolean | null) => {
        if (!clinicaId) return;
        if (ativo === false) {
            const ok = await confirmar({
                titulo: `Desligar ${flag.descricao} para ${clinica?.nome ?? 'esta clínica'}?`,
                descricao: 'O módulo some do menu e da rota dessa clínica na hora, mesmo com alguém usando.',
                confirmar: 'Desligar',
            });
            if (!ok) return;
        }
        setEmCurso(flag.id);
        try {
            await dados().definirExcecaoDaFlag(flag.id, clinicaId, ativo);
            aviso({
                title: ativo === null ? 'Exceção removida' : ativo ? 'Ligado para esta clínica' : 'Desligado para esta clínica',
                description: `${flag.descricao}${ativo === null ? ' · volta a seguir o padrão' : ''}`,
            });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível mudar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setEmCurso(null);
        }
    };

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {confirmacao}
            <PageHeader
                title="Feature flags"
                description="Ligam e desligam módulos do produto da clínica — e o efeito é imediato no menu e na rota."
            />

            {/* Uma flag que nada lê é interruptor que não faz nada. Estas
                mudam o produto de quem está do outro lado, e a tela diz isso
                antes de alguém desligar por curiosidade. */}
            <Alert tone="warning" dismissible={false} title="Desligar aqui tira o módulo da clínica">
                O item some do menu dela e a rota passa a responder “não habilitado” — 404, e não 403, porque o
                certo é “esta clínica não tem”, não “existe e você não pode”. Agenda, contatos, atendimentos e
                financeiro não têm flag: clínica sem agenda não é um módulo a menos, é outro produto.
            </Alert>

            <Alert tone="neutral" dismissible={false} title="Quem manda, em ordem">
                Kill switch, depois a exceção da clínica, depois o rollout por porcentagem, e só então o padrão da
                base. Enquanto houver rollout, o padrão não vale.
            </Alert>

            <Card
                title="Módulos"
                subtitle="O estado de cada flag na base, e a exceção da clínica escolhida"
                padding={18}
                actions={(
                    <Select
                        value={clinicaId} onChange={setClinicaId}
                        options={carga.clinicas.map((c) => ({ value: c.id, label: c.nome }))}
                        size="sm" style={{ minWidth: 'min(220px, 100%)' }}
                        aria-label="Clínica das exceções"
                    />
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {carga.flags.map((flag) => {
                        const excecao = flag.excecoes.find((e) => e.clinicaId === clinicaId);
                        const rota = rotaPorId(flag.modulo);
                        const ligada = flagLigadaPara(flag, clinicaId);
                        const comRollout = flag.rolloutPercentual !== undefined;
                        return (
                            <div
                                key={flag.id}
                                style={{
                                    display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(220px,100%),1fr))',
                                    gap: 14, alignItems: 'center',
                                    paddingTop: 12, borderTop: '1px solid var(--border)',
                                }}
                            >
                                <span>
                                    <strong style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, flexWrap: 'wrap' }}>
                                        <Icon name={rota?.icon ?? 'flag'} size={15} />
                                        {rota?.label ?? flag.modulo}
                                        {flag.desligadaEm && <Badge tone="danger">kill switch</Badge>}
                                        {comRollout && <Badge tone="info">rollout {flag.rolloutPercentual}%</Badge>}
                                        {excecao && <Badge tone="info">exceção</Badge>}
                                    </strong>
                                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>
                                        {flag.descricao} · <code>{flag.chave}</code>
                                    </span>
                                    {flag.ligadas !== undefined && flag.total !== undefined && (
                                        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)' }}>
                                            ligado em {flag.ligadas} de {flag.total} clínicas
                                        </span>
                                    )}
                                </span>

                                <Switch
                                    checked={flag.ativoPadrao}
                                    disabled={emCurso === flag.id || comRollout || Boolean(flag.desligadaEm)}
                                    label="Padrão da base"
                                    description={comRollout
                                        ? 'sem efeito enquanto houver rollout'
                                        : flag.ativoPadrao ? 'ligado para quem não tem exceção' : 'desligado para quem não tem exceção'}
                                    onChange={(v) => { void mudarPadrao(flag, v); }}
                                />

                                <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                        {clinica?.nome ? `${ligada ? 'ligado' : 'desligado'} aqui` : ''}
                                    </span>
                                    <Button
                                        size="sm" variant="secondary" disabled={emCurso === flag.id || Boolean(flag.desligadaEm)}
                                        onClick={() => { void mudarExcecao(flag, !ligada); }}
                                    >
                                        {ligada ? 'Desligar' : 'Ligar'}
                                    </Button>
                                    {excecao && (
                                        <Button size="sm" variant="ghost" disabled={emCurso === flag.id} onClick={() => { void mudarExcecao(flag, null); }}>
                                            Seguir padrão
                                        </Button>
                                    )}
                                    {alterna && (
                                        <>
                                            <Button size="sm" variant="ghost" onClick={() => setRollout(flag)}>Rollout</Button>
                                            <Button size="sm" variant={flag.desligadaEm ? 'secondary' : 'ghost'} onClick={() => setKill(flag)}>
                                                {flag.desligadaEm ? 'Religar' : 'Kill switch'}
                                            </Button>
                                        </>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </Card>

            <RolloutModal flag={rollout} total={carga.clinicas.length} onFechar={() => setRollout(null)} />
            <KillSwitchModal flag={kill} onFechar={() => setKill(null)} />
        </div>
    );
}
